import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '@/store/i18n';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { toast } from '@/components/ui/Toast';
import { api } from '@/lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
  cart: number[];
  metadata?: Record<string, string>;
}

export function SaveExamSetModal({ open, onClose, cart, metadata }: Props) {
  const t = useT();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');

  const save = useMutation({
    mutationFn: () =>
      api('exam-sets/index.php', {
        method: 'POST',
        json: { name: name.trim(), description: description.trim() || undefined, question_ids: cart, metadata: metadata ?? {} },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exam-sets'] });
      toast.success(t('set.success_save'));
      setName('');
      setDescription('');
      onClose();
    },
    onError: () => toast.error(t('set.fail_save')),
  });

  function handleSave() {
    if (!name.trim()) { setNameError(t('set.field.name')); return; }
    setNameError('');
    save.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('set.save_title')}
      description={t('set.save_desc')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('btn.cancel')}</Button>
          <Button onClick={handleSave} loading={save.isPending}>{t('set.btn.save')}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label={t('set.field.name')}
          placeholder={t('set.field.name_ph')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={nameError}
        />
        <Textarea
          label={t('set.field.description')}
          value={description}
          rows={2}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
    </Modal>
  );
}
